import { createFileRoute, Link } from "@tanstack/react-router";
import { SiteShell } from "@/components/site/SiteShell";
import { Section, Breadcrumbs, PageHero } from "@/components/site/Sections";
import { LOCATIONS } from "@/components/site/locations";

const TITLE = "Elektriker i byer og områder | Elektriker-Elektriker";
const DESCRIPTION = "Komplet oversigt over alle byer og bydele i København og Storkøbenhavn hvor vi rykker ud som autoriseret elektriker. Find din by og se typisk udrykning, lokale opgaver og kontakt.";

export const Route = createFileRoute("/byer")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:url", content: "/byer" },
    ],
    links: [{ rel: "canonical", href: "https://elektriker-elektriker.dk/byer" }],
  }),
  component: ByerPage,
});

// Gruppér byer logisk efter geografisk område
const GROUPS: { title: string; description: string; slugs: string[] }[] = [
  {
    title: "København by",
    description: "Brokvartererne og de centrale bydele.",
    slugs: [
      "elektriker-koebenhavn",
      "elektriker-indre-by",
      "elektriker-oesterbro",
      "elektriker-norrebro",
      "elektriker-vesterbro",
      "elektriker-christianshavn",
      "elektriker-nordhavn",
      "elektriker-sydhavn",
      "elektriker-nordvest",
      "elektriker-broenshoej",
      "elektriker-frederiksberg",
      "elektriker-vanloese",
      "elektriker-valby",
    ],
  },
  {
    title: "Amager",
    description: "Hele Amager fra Islands Brygge til Dragør.",
    slugs: [
      "elektriker-amager",
      "elektriker-kastrup",
      "elektriker-dragoer",
    ],
  },
  {
    title: "Nord for København",
    description: "Gentofte, Lyngby-Taarbæk og Rudersdal kommuner.",
    slugs: [
      "elektriker-hellerup",
      "elektriker-gentofte",
      "elektriker-charlottenlund",
      "elektriker-skovshoved",
      "elektriker-klampenborg",
      "elektriker-vedbaek",
      "elektriker-lyngby",
      "elektriker-virum",
      "elektriker-holte",
      "elektriker-soborg",
      "elektriker-bagsvaerd",
    ],
  },
  {
    title: "Vest for København",
    description: "Vestegnen og det store erhvervsbælte.",
    slugs: [
      "elektriker-gladsaxe",
      "elektriker-herlev",
      "elektriker-skovlunde",
      "elektriker-ballerup",
      "elektriker-roedovre",
      "elektriker-hvidovre",
    ],
  },
  {
    title: "Nordsjælland",
    description: "Hørsholm, Birkerød og det øvrige Nordsjælland.",
    slugs: [
      "elektriker-hoersholm",
      "elektriker-birkeroed",
      "elektriker-nordsjaelland",
    ],
  },
  {
    title: "Syd for København",
    description: "Køge Bugt og området mod Sydsjælland.",
    slugs: [
      "elektriker-greve",
    ],
  },
];

function ByerPage() {
  return (
    <SiteShell>
      <Breadcrumbs items={[{ label: "Forside", to: "/" }, { label: "Byer" }]} />
      <PageHero
        variant="centered"
        eyebrow="Lokal elektriker · Storkøbenhavn"
        title="Byer og områder vi dækker"
        intro={`Vi har biler i alle bydele hver hverdag og dækker hele København, Frederiksberg, Gentofte, Lyngby, Vestegnen og det meste af Nordsjælland. Find din by herunder og se typisk udrykning, lokale opgaver og kontakt for dit område. Vi dækker ${LOCATIONS.length} byer og områder i alt.`}
      />

      <Section>
        <div className="space-y-16">
          {GROUPS.map((group) => {
            const cities = group.slugs
              .map((slug) => LOCATIONS.find((l) => l.slug === slug))
              .filter((l): l is NonNullable<typeof l> => Boolean(l));
            if (cities.length === 0) return null;
            return (
              <div key={group.title}>
                <h2 className="text-2xl font-bold text-[var(--ink)]">{group.title}</h2>
                <p className="mt-2 text-muted-foreground">{group.description}</p>
                <div className="mt-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                  {cities.map((c) => (
                    <Link
                      key={c.slug}
                      to={`/${c.slug}` as string}
                      className="group block rounded-2xl border border-border bg-card p-5 transition hover:border-primary hover:shadow-[var(--shadow-soft)]"
                    >
                      <div className="flex items-baseline justify-between gap-3">
                        <h3 className="text-lg font-semibold text-[var(--ink)] group-hover:text-primary">
                          Elektriker {c.city}
                        </h3>
                        {c.postcode ? (
                          <span className="shrink-0 text-xs text-muted-foreground">{c.postcode}</span>
                        ) : null}
                      </div>
                      {c.responseMinutes ? (
                        <p className="mt-2 text-xs text-muted-foreground">
                          Typisk udrykning: {c.responseMinutes} min.
                        </p>
                      ) : null}
                      {c.typicalJobs && c.typicalJobs.length > 0 ? (
                        <p className="mt-3 line-clamp-2 text-sm text-[var(--ink)]/75">
                          {c.typicalJobs.slice(0, 3).join(", ")}
                        </p>
                      ) : null}
                    </Link>
                  ))}
                </div>
              </div>
            );
          })}
        </div>

        <div className="mt-16 rounded-2xl border border-border bg-secondary p-8 text-center">
          <h2 className="text-xl font-bold text-[var(--ink)]">Er din by ikke på listen?</h2>
          <p className="mt-2 text-muted-foreground">
            Vi kører i hele Storkøbenhavn og store dele af Nordsjælland. Ring og hør om vi kan rykke ud hos dig.
          </p>
          <div className="mt-5 flex flex-wrap items-center justify-center gap-3">
            <a
              href="tel:+4528700028"
              className="inline-flex items-center justify-center rounded-xl bg-primary px-5 py-3 text-sm font-semibold text-primary-foreground shadow-[var(--shadow-soft)] transition hover:opacity-90"
            >
              Ring +45 28 7000 28
            </a>
            <Link
              to="/kontakt"
              className="inline-flex items-center justify-center rounded-xl border border-border bg-background px-5 py-3 text-sm font-semibold text-[var(--ink)] transition hover:border-primary"
            >
              Skriv til os
            </Link>
          </div>
        </div>
      </Section>
    </SiteShell>
  );
}
